import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeader, formatDateDDMMYYYY } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Users, UserPlus, Mail, Shield, Building2, Edit, Trash2, 
  CheckCircle2, XCircle, Search, Filter, MoreVertical, UserCog
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

const TeamsManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTeam, setFilterTeam] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMoveTeam, setBulkMoveTeam] = useState("");
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    team: "",
    role: "member"
  });

  const teams = [
    { value: "Account", label: "Account Team", color: "bg-blue-500", icon: "💼" },
    { value: "Processing", label: "Processing Team", color: "bg-purple-500", icon: "⚙️" },
    { value: "BackOffice", label: "Back Office Team", color: "bg-green-500", icon: "📋" },
    { value: "ITDept", label: "IT Department", color: "bg-orange-500", icon: "💻" },
    { value: "Partners", label: "Partners Team", color: "bg-pink-500", icon: "🤝" }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/create-user`, newUser, { headers: getAuthHeader() });
      toast.success("User created successfully!");
      setCreateDialogOpen(false);
      setNewUser({ email: "", password: "", name: "", team: "", role: "member" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, {
        name: selectedUser.name,
        team: selectedUser.team,
        role: selectedUser.role
      }, { headers: getAuthHeader() });
      toast.success("User updated successfully!");
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success("User deleted successfully!");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleApproveUser = async (userId, approved) => {
    try {
      await axios.post(`${API}/admin/approve-user`, { user_id: userId, approved }, { headers: getAuthHeader() });
      toast.success(approved ? "User approved!" : "User denied!");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleMoveToTeam = async (userId, newTeam) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { team: newTeam }, { headers: getAuthHeader() });
      toast.success(`User moved to ${newTeam}!`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to move user");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleBulkAction = async (action, team) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const label = action === 'approve' ? 'approve' : action === 'delete' ? 'delete' : action === 'reject' ? 'reject' : `move to ${team}`;
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${ids.length} user(s)?`)) return;
    try {
      const res = await axios.post(`${API}/admin/bulk-action`, { user_ids: ids, action, team }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    }
  };

  const getTeamColor = (team) => {
    const t = teams.find(tm => tm.value === team);
    return t ? t.color : "bg-slate-500";
  };

  const getTeamIcon = (team) => {
    const t = teams.find(tm => tm.value === team);
    return t ? t.icon : "👤";
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTeam = filterTeam === "all" || user.team === filterTeam;
    const matchesStatus = filterStatus === "all" || 
                          (filterStatus === "approved" && user.approved) ||
                          (filterStatus === "pending" && !user.approved);
    const matchesTab = activeTab === "all" || user.team === activeTab;
    
    return matchesSearch && matchesTeam && matchesStatus && matchesTab;
  });

  // Team statistics
  const teamStats = teams.map(team => ({
    ...team,
    count: users.filter(u => u.team === team.value).length,
    approved: users.filter(u => u.team === team.value && u.approved).length,
    pending: users.filter(u => u.team === team.value && !u.approved).length
  }));

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatDateDDMMYYYY(dateString);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading teams...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Team Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {teamStats.map(team => (
          <Card 
            key={team.value}
            className={`cursor-pointer transition-all hover:shadow-lg ${activeTab === team.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
            onClick={() => setActiveTab(activeTab === team.value ? "all" : team.value)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 ${team.color} rounded-xl flex items-center justify-center text-white text-xl`}>
                  {team.icon}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-900">{team.count}</p>
                  <p className="text-xs text-slate-500">{team.label.replace(' Team', '')}</p>
                </div>
              </div>
              <div className="mt-2 flex justify-end gap-2 text-xs">
                <span className="text-green-600">{team.approved} active</span>
                {team.pending > 0 && <span className="text-amber-600">{team.pending} pending</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Panel */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Teams Management
                </CardTitle>
                <CardDescription>
                  {filteredUsers.length} members {activeTab !== "all" ? `in ${activeTab}` : "total"}
                </CardDescription>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>

              {/* Add User Button */}
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Team Member</DialogTitle>
                    <DialogDescription>
                      Create a new user account and assign to a team
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@company.com"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="team">Team</Label>
                        <Select value={newUser.team} onValueChange={(v) => setNewUser({ ...newUser, team: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select team" />
                          </SelectTrigger>
                          <SelectContent>
                            {teams.map(team => (
                              <SelectItem key={team.value} value={team.value}>
                                {team.icon} {team.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white">
                      Create Member
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Team Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="all" className="data-[state=active]:bg-white">
                All Teams ({users.length})
              </TabsTrigger>
              {teams.map(team => (
                <TabsTrigger key={team.value} value={team.value} className="data-[state=active]:bg-white">
                  {team.icon} {team.value} ({teamStats.find(t => t.value === team.value)?.count || 0})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="teams-bulk-bar">
              <span className="text-sm font-medium text-indigo-800">{selectedIds.size} selected</span>
              <div className="h-4 w-px bg-indigo-300" />
              <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50" data-testid="teams-bulk-approve" onClick={() => handleBulkAction('approve')}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50" data-testid="teams-bulk-delete" onClick={() => handleBulkAction('delete')}>
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
              <div className="flex items-center gap-1">
                <Select value={bulkMoveTeam} onValueChange={setBulkMoveTeam}>
                  <SelectTrigger className="h-7 w-36 text-xs" data-testid="teams-bulk-move-select">
                    <SelectValue placeholder="Move to team..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(t => <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {bulkMoveTeam && (
                  <Button size="sm" variant="outline" className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="teams-bulk-move-btn" onClick={() => { handleBulkAction('move_team', bulkMoveTeam); setBulkMoveTeam(''); }}>
                    Move
                  </Button>
                )}
              </div>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 ml-auto" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
            </div>
          )}

          {/* Users Table */}
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No members found</p>
              <p className="text-sm text-slate-500 mt-1">
                {searchTerm ? "Try a different search term" : "Add your first team member"}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={filteredUsers.length > 0 && selectedIds.size === filteredUsers.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="teams-select-all"
                      />
                    </TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map(user => (
                    <TableRow key={user.id} className={`group hover:bg-slate-50 ${selectedIds.has(user.id) ? 'bg-indigo-50/50' : ''}`}>
                      <TableCell className="w-10">
                        <Checkbox
                          checked={selectedIds.has(user.id)}
                          onCheckedChange={() => toggleSelect(user.id)}
                          data-testid={`teams-select-${user.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback className={`${getTeamColor(user.team)} text-white font-semibold`}>
                              {user.name?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{user.name}</p>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getTeamColor(user.team)} text-white border-0`}>
                          {getTeamIcon(user.team)} {user.team}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={user.role === 'admin' ? 'border-red-200 text-red-700 bg-red-50' : ''}>
                          {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.approved ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            <XCircle className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedUser(user); setEditDialogOpen(true); }}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            
                            {!user.approved && (
                              <>
                                <DropdownMenuItem onClick={() => handleApproveUser(user.id, true)} className="text-green-600">
                                  <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleApproveUser(user.id, false)} className="text-red-600">
                                  <XCircle className="w-4 h-4 mr-2" /> Deny
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {/* Move to Team submenu */}
                            <DropdownMenuItem className="p-0">
                              <DropdownMenu>
                                <DropdownMenuTrigger className="flex items-center px-2 py-1.5 w-full">
                                  <Building2 className="w-4 h-4 mr-2" /> Move to Team
                                </DropdownMenuTrigger>
                                <DropdownMenuContent side="left">
                                  {teams.filter(t => t.value !== user.team).map(team => (
                                    <DropdownMenuItem key={team.value} onClick={() => handleMoveToTeam(user.id, team.value)}>
                                      {team.icon} {team.label}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </DropdownMenuItem>
                            
                            {user.email !== 'admin@taskflow.com' && (
                              <DropdownMenuItem onClick={() => handleDeleteUser(user.id, user.email)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Edit User Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Team Member</DialogTitle>
                <DialogDescription>Update member details and team assignment</DialogDescription>
              </DialogHeader>
              {selectedUser && (
                <form onSubmit={handleUpdateUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={selectedUser.name}
                      onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={selectedUser.email} disabled className="bg-slate-100" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Team</Label>
                      <Select value={selectedUser.team} onValueChange={(v) => setSelectedUser({ ...selectedUser, team: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {teams.map(team => (
                            <SelectItem key={team.value} value={team.value}>
                              {team.icon} {team.label}
                            </SelectItem>
                          ))}
                          <SelectItem value="Admin">👑 Admin Panel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select 
                        value={selectedUser.role} 
                        onValueChange={(v) => setSelectedUser({ ...selectedUser, role: v })}
                        disabled={selectedUser.email === 'admin@taskflow.com'}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white">
                      Save Changes
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamsManagementPanel;
